
/**
 * Composition method for moa indicator moaule
 * 
 * Author: Sahil David
 */
 import { computed, onMounted, ref, watch } from 'vue';
 import { useStore } from 'vuex';
 import { useRoute, useRouter } from 'vue-router';

export function moaIndicatorComposition (datum: any) {
	const store = useStore();
	const route = useRoute();
	const router = useRouter();

	// Data
	const scrollIndicatorWait = ref(500);

	// Computed active section
	const activeSection = computed((): string => store.getters.get_moaActiveSection);
	const activeFrame = computed((): string => store.getters.get_moaActiveFrame);

	const frameStatus = computed((): string => {
		const currentActiveIndex = datum.value.findIndex((_: any) => _.endpointId === activeFrame.value);
		if (currentActiveIndex === 0) { return 'start'; }
		if (currentActiveIndex === datum.value.length - 1) { return 'end'; }

		return 'middle';
	});
	
	// Scroll Indicator function 
	function scrollIndicator (): void {
		const frameRecord = datum.value.find((_: any) => _.endpointId === activeFrame.value);
		const section = (frameRecord || {}).moa;

		const mainFrame = document.querySelector('.js-timeline-container-1')
		const portrait = mainFrame && mainFrame.classList.contains('res-screen-portrait');

		// Reset all sections
		const currentActiveIndex = datum.value.findIndex((_: any) => _.endpointId === activeFrame.value);
		(datum.value || []).forEach((frame: any, index: number) => {
			const frameSection: any = document.querySelector(`[data-section="${frame.moa}"]`);
			frameSection.style.width = '';
			frameSection.style.height = '';
			if (index <= currentActiveIndex) {
				if (portrait) { frameSection.style.width = '100%'; } 
				else { frameSection.style.height = '100%'; }
			} else {
				if (portrait) { frameSection.style.width = '0%'; } 
				else { frameSection.style.height = '0%'; }
			}
		});

		const totalSectionRecords = datum.value.filter((_: any) => _.moa === section);
		const totalLength = (totalSectionRecords || []).length;
		const currentIndex = totalSectionRecords.findIndex((_: any) => _.endpointId === activeFrame.value);
		if (totalLength > 0) {
			const percentSection = ((currentIndex + 1) / totalLength) * 100;
			const currentIndicator: any = document.querySelector(`[data-section="${section}"]`);
			currentIndicator.style.width = '';
			currentIndicator.style.height = '';
			if (portrait) { currentIndicator.style.width = `${percentSection}%`; }
			else { currentIndicator.style.height = `${percentSection}%`; }
		}
	}

	// Click Indicator function 
	function navigateToSection (sectionId: string) {
		const frame = datum.value.find((_: any) => _.moa === sectionId);
		if (!frame) { return; }

		const element: any = document.querySelector(`[data-frame="${frame.endpointId}"]`);
		if (!element) { return; }

		document.querySelector('.js-timeline-window')?.scroll({
			top: (element as any).offsetTop || 0,
			left: 0,
		});

		// reset scroll indicator after click
		setTimeout(scrollIndicator, scrollIndicatorWait.value);
	}

	// Navigate to frame based on the params
	function navigateToFrame (direction: number, forcedIndex?: number) {
		let index = 0;
		if (forcedIndex !== undefined) {
			index = forcedIndex;
		} else {
			const currentIndex = datum.value.findIndex((_: any) => _.endpointId === activeFrame.value);
			if (currentIndex === 0 && direction === -1) { return; }
			if (currentIndex === datum.value.length -1 && direction === 1) { return; }
			index = currentIndex + direction;
		}

		const sectionTo = datum.value[index];
		if (!sectionTo) { return; }

		const element: any = document.querySelector(`[data-frame="${sectionTo.endpointId}"]`);
		if (!element) { return; }								

		document.querySelector('.js-timeline-window')?.scroll({
			top: (element as any).offsetTop || 0,
			left: 0,
		});

		// reset scroll indicator after click
		setTimeout(scrollIndicator, scrollIndicatorWait.value);
	}

	// Key Listeners 
	function keyListener () {
		window.addEventListener('keydown', (e) => {
			const { key } = e;
			if (!key) { return; }

			if (['ArrowLeft', 'ArrowUp'].includes(key)) {
				navigateToFrame(-1);
			} else if (['ArrowRight', 'ArrowDown'].includes(key)) {
				navigateToFrame(1);
			} else if (['r'].includes(key)) {
				navigateToFrame(0, 0);
			}
		});
	}

	// Query listener and navigation
	function queryListener () {
		if (!route.query) { return; }

		const { frame } = route.query;
		if (!frame) { return; }

		const index = datum.value.findIndex((_: any) => _.endpointId === frame);
		if (index < 0) { return; }

		store.dispatch('axn_moaUpdateScreen', 'timeline');
		navigateToFrame(0, index);
		router.push({
			query: {}
		});
	}

	// Watcher
	watch(() => route.query, () => {
		queryListener();
	})

	// Mounted lifecycle
	onMounted(() => {
		const scrollWindow = document.querySelector('.js-timeline-window');

		scrollIndicator();
		scrollWindow?.addEventListener('scroll', () => {
			scrollIndicator();
		});

		// Key Listener
		keyListener();

		// Query listener
		queryListener();
	});

	return { frameStatus, activeSection, navigateToSection, navigateToFrame }
}
